import axiosConfig from "./axiosConfig";

// Kicked-off response. `status: "pending"` when a batch was staged (poll the
// operationKey); `status: "noop"` when every selected row was already researched
// (nothing to run, no operationKey).
export interface BulkLandlordResearchResponse {
  status: "pending" | "noop";
  operationKey?: string;
  siteId?: string;
  // True when an identical in-flight batch was coalesced onto rather than
  // spawning a second paid run.
  deduped?: boolean;
  // Rows the batch will research (every selected row that exists, within the cap).
  rowCount: number;
  // Rows beyond the per-run cap - the operator re-runs for these.
  overflowCount: number;
  // The refs ACTUALLY queued (eligible-filtered + capped, so a subset of the
  // selection when the operator picked more than the cap or some rows were
  // ineligible). The panel keys its "researching" indicator off THIS, never the
  // full selection, so overflow rows the operator must re-run aren't shown busy.
  queuedRefs?: string[];
}

// The terminal `result` shape read from /operations/{key} once the bulk op
// finishes (mirrors the BE worker's return).
export interface BulkLandlordResearchResult {
  status: "complete" | "not_configured";
  total: number;
  researched: string[];
  researchedCount: number;
  skipped: string[];
  failed: { sourceRef: string; status?: string; error?: string }[];
  notProcessed: string[];
}

// Kick off bulk agentic landlord research over the selected /leads/search rows
// as one long-running operation. The BE filters to rows not already researched,
// caps the batch, and returns immediately; the FE polls useOperation(...) until
// terminal, then re-runs the search so the researched candidates show.
const postVoaResearchLandlordBulk = async (
  authToken: string,
  sourceRefs: string[]
): Promise<BulkLandlordResearchResponse> => {
  const { data } = await axiosConfig.post(
    `/leads/voa-research-landlord-bulk`,
    { sourceRefs },
    { headers: { Authorization: `Bearer ${authToken}` } }
  );
  return data;
};

// Sibling of the above for the /leads inbox: researches the selected promoted
// leads by lead id (subjectKind "lead" on the BE), so the result lands on each
// lead's agenticData which the inbox reads. Same LRO + response shape.
export const postLeadsResearchLandlordBulk = async (
  authToken: string,
  leadIds: string[]
): Promise<BulkLandlordResearchResponse> => {
  const { data } = await axiosConfig.post(
    `/leads/research-landlord-bulk`,
    { sourceRefs: leadIds },
    { headers: { Authorization: `Bearer ${authToken}` } }
  );
  return data;
};

export default postVoaResearchLandlordBulk;
